// preprocess.ts — Markdown preprocessing pipeline.
//
// Extracted from markdown-renderer.js (Stage A of the monolith split). A
// referentially-transparent string→string transform: split the source into
// lines, run each line-level pass, join, then apply the text-level footnote
// pass. preprocessMarkdown is the only export; the 11 passes stay private.
//
// `marked` is a runtime global (the vendored markdown lib), same as in the
// host — the inline-list/lettered-list/footnote passes call marked.parseInline.

// Matches the opening/closing line of a fenced code block (``` or ~~~).
const _FENCE_RE = /^[ \t]*(`{3,}|~{3,})/;

// Fence-aware line mapping. The function fn(line, i, lines) is called
// only for lines OUTSIDE fenced code blocks. Fence-opening/closing lines
// and the lines inside fences pass through untouched. Used by every
// pass that's a pure line-to-line transformation.
function _fenceAwareMap(
  lines: string[],
  fn: (line: string, i: number, lines: string[]) => string
): string[] {
  let inFence = false;
  const out = new Array(lines.length);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (_FENCE_RE.test(line)) {
      inFence = !inFence;
      out[i] = line;
      continue;
    }
    if (inFence) {
      out[i] = line;
      continue;
    }
    out[i] = fn(line, i, lines);
  }
  return out;
}

// Protect inline code spans (`…`) from a line-level transform: replace each
// span with a \x01<idx>\x01 sentinel, run `transform` on the remaining text,
// then restore the spans. Shared by the underscore-italic and highlight passes
// so both classify "this is inside inline code" identically.
function _withProtectedCodeSpans(line: string, transform: (s: string) => string): string {
  const codes: string[] = [];
  let safe = line.replace(/`[^`\n]+`/g, (m) => {
    codes.push(m);
    return `\x01${codes.length - 1}\x01`;
  });
  safe = transform(safe);
  // biome-ignore lint/suspicious/noControlCharactersInRegex: \x01 is an intentional placeholder sentinel wrapping protected code spans (paired with the push above).
  safe = safe.replace(/\x01(\d+)\x01/g, (_, i) => codes[+i]);
  return safe;
}

// ── 1. Disable underscore italics (_text_) ──
// Escape lone underscores so marked doesn't treat them as emphasis.
// Keeps __bold__ and ___bold-italic___ intact. Skips code fences and inline code.
function _escapeUnderscoreItalics(lines: string[]): string[] {
  return _fenceAwareMap(lines, (line) =>
    _withProtectedCodeSpans(line, (safe) => safe.replace(/(?<![_\\])_(?!_)/g, '\\_'))
  );
}

// ── 2. Fix setext headings: insert blank line before "---"/"===" that follow text ──
// Without this, "Some text\n---" is parsed as an h2 instead of a horizontal rule.
function _ensureSetextBreak(lines: string[]): string[] {
  let inFence = false;
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (_FENCE_RE.test(line)) inFence = !inFence;
    if (
      !inFence &&
      /^[ \t]*(-{3,}|={3,})[ \t]*$/.test(line) &&
      i > 0 &&
      out.length > 0 &&
      out[out.length - 1].trim() !== ''
    ) {
      out.push('');
    }
    out.push(line);
  }
  return out;
}

// ── 3. Wiki links (supports [[note##heading]] heading-link syntax) ──
// Two or more consecutive hashes separate the note name from the heading text.
// The heading is stripped when resolving the target note for graph/link purposes.
// Fence-aware so [[...]] inside fenced code blocks stays literal.
function _expandWikiLinks(lines: string[]): string[] {
  return _fenceAwareMap(lines, (line) =>
    line.replace(/\[\[([^\]]+)\]\]/g, (_, inner) => {
      // `_escapeUnderscoreItalics` (pass 1) escaped lone underscores to `\_`
      // before this pass runs; undo that inside the wiki-link inner so the
      // `_`→space display + `encodeURIComponent` href below aren't contaminated
      // by a stray backslash (which would render "My\ Note" and a broken href).
      const trimmed = inner.replace(/\\_/g, '_').trim();
      const hashMatch = trimmed.match(/^([^#]+)(#{2,})(.+)$/);
      if (hashMatch) {
        const notePart = hashMatch[1].trim();
        const headingPart = hashMatch[3].trim();
        const display = `${notePart.replace(/_/g, ' ')} › ${headingPart.replace(/_/g, ' ')}`;
        const href = encodeURIComponent(trimmed);
        return `[${display}](${href})`;
      }
      const display = trimmed.replace(/_/g, ' ');
      const href = encodeURIComponent(trimmed);
      return `[${display}](${href})`;
    })
  );
}

// ── 4. Strip schedule syntax from end of lines ──
// Handles: > YYMMDD HHMM HHMM  (timed)
//          > YYMMDD YYMMDD      (multi-day)
//          > YYMMDD             (all-day)
//          > YYMMDD @Calendar   (with calendar name)
//          > YYMMDD … ~rrule    (recurrence token, e.g. ~weekly)
// Fence-aware so a line like "> 250101" inside a code block stays literal.
// `@cal` and `~rrule` may appear in either order; both are stripped.
function _stripScheduleSyntax(lines: string[]): string[] {
  const schedRe =
    /\s*>\s*\d{6}(?:\s+(?:\d{6}|\d{4}\s+\d{4}))?(?:\s+~\S+)?(?:\s+@\S+)?(?:\s+~\S+)?\s*$/;
  const out = [];
  let inFence = false;
  for (let si = 0; si < lines.length; si++) {
    const line = lines[si];
    if (_FENCE_RE.test(line)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    if (schedRe.test(line)) {
      let stripped = line.replace(schedRe, '');
      const trimmed = stripped.trimStart();
      const isTask = /^- \[[ xX]\]\s/.test(trimmed);
      const isList =
        /^[-*+]\s/.test(trimmed) || /^\d+[.)]\s/.test(trimmed) || /^[a-zA-Z][.)]\s/.test(trimmed);
      const isHeading = /^#+\s/.test(trimmed);
      if (!isTask) {
        if (isHeading) {
          stripped = stripped.replace(/(#+\s)/, '$1🗓️ ');
        } else if (isList) {
          stripped = stripped.replace(/^(\s*(?:[-*+]|\d+[.)]|[a-zA-Z][.)]) )/, '$1🗓️ ');
        } else {
          stripped = stripped.replace(/^(\s*)/, '$1🗓️ ');
        }
      }
      out.push(stripped);
      const nextLine = lines[si + 1];
      const nextIsBlank = nextLine === undefined || nextLine.trim() === '';
      if (!isList && !isHeading && !nextIsBlank) {
        out.push('');
      }
    } else {
      out.push(line);
    }
  }
  return out;
}

// ── 5. Auto-collapse headings: strip trailing ">" and inject collapse marker ──
// Fence-aware so a fenced code block containing "## Heading >" is left alone.
function _autoCollapseHeadings(lines: string[]): string[] {
  return _fenceAwareMap(lines, (line) => {
    const trimmed = line.trimStart();
    if (!/^#{1,6}\s/.test(trimmed)) return line;
    if (!/\s*>\s*$/.test(line)) return line;
    const stripped = line.replace(/\s*>\s*$/, '');
    return `${stripped}<span class="collapse-marker" style="display:none"></span>`;
  });
}

// ── 6. Highlight syntax ==text== → <mark>text</mark> ──
// Fence- and inline-code-aware so backtick-wrapped ==text== stays literal.
function _injectHighlightMarks(lines: string[]): string[] {
  return _fenceAwareMap(lines, (line) =>
    _withProtectedCodeSpans(line, (safe) => safe.replace(/==([^=\n]+)==/g, '<mark>$1</mark>'))
  );
}

// ── 7. Empty blockquote lines: ensure bare ">" renders as an empty blockquote ──
// A ">" with nothing after it (or only spaces/tabs) produces no visible output
// from the parser. Appending a zero-width space (U+200B) gives the parser
// non-empty content while keeping the rendered line visually blank.
// Fence-aware so a literal ">" inside a code block isn't mutated.
function _padEmptyBlockquotes(lines: string[]): string[] {
  return _fenceAwareMap(lines, (line) => line.replace(/^( {0,3}>)[ \t]*$/, '$1 \u200B'));
}

// ── 8. Indentation: convert leading tabs into padded HTML blocks ──
function _wrapTabIndentation(lines: string[]): string[] {
  const out = [];
  let inFence = false;
  let pendingList: any = null;
  let prevWasListItem = false;
  let prevWasBlankLine = false;

  const flushPendingList = () => {
    if (!pendingList) return;
    const { baseDepth, listLines } = pendingList;
    const padEm = baseDepth * 2;
    out.push(`<div style="padding-left:${padEm}em">`);
    out.push('');
    listLines.forEach((l: any) => {
      out.push(l);
    });
    out.push('');
    out.push('</div>');
    out.push('');
    pendingList = null;
  };

  for (const line of lines) {
    if (_FENCE_RE.test(line)) {
      flushPendingList();
      inFence = !inFence;
      out.push(line);
      prevWasListItem = false;
      prevWasBlankLine = false;
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }

    if (line.trim() === '') {
      flushPendingList();
      out.push(line);
      prevWasListItem = false;
      prevWasBlankLine = true;
      continue;
    }

    const wasBlankBefore = prevWasBlankLine;
    prevWasBlankLine = false;

    const tabMatch = line.match(/^(\t+)(.*)/);
    if (tabMatch) {
      const depth = tabMatch[1].length;
      const content = tabMatch[2];
      const trimmed = content.trimStart();
      const isListItem =
        /^[-*+]\s/.test(trimmed) ||
        /^\d+[.)]\s/.test(trimmed) ||
        /^[a-zA-Z][.)]\s/.test(trimmed) ||
        /^\[[ xX]\]\s/.test(trimmed);
      const isBlockquote = trimmed.startsWith('>');
      const isHeading = trimmed.startsWith('#');

      if (isListItem) {
        if (!prevWasListItem) {
          flushPendingList();
          pendingList = { baseDepth: depth, listLines: [] };
        }

        if (pendingList) {
          const relativeDepth = depth - pendingList.baseDepth;
          pendingList.listLines.push('    '.repeat(Math.max(0, relativeDepth)) + content);
        } else {
          out.push('    '.repeat(depth) + content);
        }
        prevWasListItem = true;
      } else if (isBlockquote || isHeading) {
        flushPendingList();
        out.push('    '.repeat(depth) + content);
        prevWasListItem = false;
      } else {
        flushPendingList();
        const rendered = window.marked.parseInline(content);
        const topMargin = wasBlankBefore ? '1em' : '0.2em';
        out.push(`<p style="padding-left:${depth * 2}em;margin:${topMargin} 0">${rendered}</p>`);
        out.push('');
        prevWasListItem = false;
      }
    } else {
      flushPendingList();
      // Do not propagate prevWasListItem from non-tab lines — tab-indented list
      // items should always start their own grouped block (div wrapper) rather
      // than being folded into a preceding non-tab list as space-indented items.
      prevWasListItem = false;
      out.push(line);
    }
  }
  flushPendingList();
  return out;
}

// ── 9. Lettered lists: a. Item / A. Item → <ol type="a/A">...</ol> ──
// Converts runs of lines starting with a single letter followed by "." or ")"
// into HTML ordered lists, matching the behaviour of numeric ordered lists.
// Works at any indentation level, including inside tab-indented <div> wrappers.
function _convertLetteredLists(lines: string[]): string[] {
  const out = [];
  let inFence = false;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (_FENCE_RE.test(line)) {
      inFence = !inFence;
      out.push(line);
      i++;
      continue;
    }
    if (inFence) {
      out.push(line);
      i++;
      continue;
    }
    const m = line.match(/^(\s*)([a-zA-Z])[.)]\s+(.*)$/);
    if (m) {
      const indent = m[1];
      const listType = /[A-Z]/.test(m[2]) ? 'A' : 'a';
      const items = [];
      let j = i;
      while (j < lines.length) {
        const ln = lines[j];
        if (_FENCE_RE.test(ln)) break;
        const lm = ln.match(/^(\s*)([a-zA-Z])[.)]\s+(.*)$/);
        if (!lm || lm[1] !== indent) break;
        items.push(window.marked.parseInline(lm[3]));
        j++;
      }
      out.push(`${indent}<ol type="${listType}">`);
      items.forEach((item) => {
        out.push(`${indent}<li>${item}</li>`);
      });
      out.push(`${indent}</ol>`);
      out.push('');
      i = j;
    } else {
      out.push(line);
      i++;
    }
  }
  return out;
}

// ── 10. Non-task checkboxes: [ ] text and [x] text (without leading "- ") ──
// These render identically to - [ ] task items (same list structure, same
// spacing) but are NOT picked up by the task list or treated as tasks.
// Consecutive plain-checkbox lines are grouped into a single <ul> so their
// spacing is indistinguishable from a run of - [ ] items.
function _convertPlainCheckboxes(lines: string[]): string[] {
  const out = [];
  let inFence = false;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (_FENCE_RE.test(line)) {
      inFence = !inFence;
      out.push(line);
      i++;
      continue;
    }
    if (inFence) {
      out.push(line);
      i++;
      continue;
    }
    // Match lines starting with [ ] or [x]/[X] that are NOT preceded by "- "
    const m = line.match(/^(\s*)\[( |[xX])\]\s(.*)$/);
    if (m && !/^(\s*)- \[/.test(line)) {
      const items = [];
      let j = i;
      while (j < lines.length) {
        const ln = lines[j];
        if (_FENCE_RE.test(ln)) break;
        const lm = ln.match(/^(\s*)\[( |[xX])\]\s(.*)$/);
        if (!lm || /^(\s*)- \[/.test(ln)) break;
        const checked = lm[2] !== ' ';
        const checkedAttr = checked ? ' checked' : '';
        // Hide {calendarId} codes at end of item (used in Settings note)
        const rawContent = lm[3].replace(
          /(\s*\{[^}]+\})\s*$/,
          '<span style="display:none">$1</span>'
        );
        const content = window.marked.parseInline(rawContent);
        items.push(`<li><input type="checkbox"${checkedAttr} data-plain-cb> ${content}</li>`);
        j++;
      }
      out.push(`<ul class="contains-task-list plain-cb-list">\n${items.join('\n')}\n</ul>`);
      i = j;
    } else {
      out.push(line);
      i++;
    }
  }
  return out;
}

export function preprocessMarkdown(text: string): string {
  // Split once; pass the array through every line-level pass; join once
  // before the footnote stage (which works on the joined text).
  let lines = text.split('\n');
  lines = _escapeUnderscoreItalics(lines);
  lines = _ensureSetextBreak(lines);
  lines = _expandWikiLinks(lines);
  lines = _stripScheduleSyntax(lines);
  lines = _autoCollapseHeadings(lines);
  lines = _injectHighlightMarks(lines);
  lines = _padEmptyBlockquotes(lines);
  lines = _wrapTabIndentation(lines);
  lines = _convertLetteredLists(lines);
  lines = _convertPlainCheckboxes(lines);
  text = lines.join('\n');

  // ── Footnotes ── (text-level, runs once on the joined result)
  // Fence-aware: re-split so [^id]: definitions and [^id] references inside
  // fenced code blocks stay literal, matching every line-level pass above.
  const defs: Record<string, any> = {};
  text = _fenceAwareMap(text.split('\n'), (line) =>
    line.replace(/^\[\^([^\]]+)\]:\s*(.+)$/, (_, id, def) => {
      defs[id] = def.trim();
      return '';
    })
  ).join('\n');

  if (Object.keys(defs).length === 0) return text;

  const order: any = [];
  text = _fenceAwareMap(text.split('\n'), (line) =>
    line.replace(/\[\^([^\]]+)\]/g, (_, id) => {
      if (!order.includes(id)) order.push(id);
      const n = order.indexOf(id) + 1;
      return `<sup><a id="fnref-${id}" href="#fn-${id}" class="footnote-ref">${n}</a></sup>`;
    })
  ).join('\n');

  const items = order
    .map((id: any, i: any) => {
      const n = i + 1;
      const defText = window.marked.parseInline(defs[id] || '');
      return `<li id="fn-${id}">${n}. ${defText} <a href="#fnref-${id}" class="footnote-back">↩</a></li>`;
    })
    .join('\n');

  text += `\n\n<hr class="footnote-hr">\n<ol class="footnotes">\n${items}\n</ol>`;

  return text;
}
